import { useState } from "react";
import { Download } from "lucide-react";
import { PageHeader } from "../../../components/PortfolioPerformance/page-header";
import { Panel } from "../../../components/PortfolioPerformance/panel";
import { Badge } from "../../../ui/badge";
import { Button } from "../../../ui/button";
import { Textarea } from "../../../ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import {
  useApproveAttestation,
  useAttestationPeriods,
  useDownloadAttestation,
  type PeriodStatus,
} from "../lib/api/attestation";
import { cn } from "../../../utils/common/cn";

function StatusBadge({ status }: { status: PeriodStatus }) {
  return (
    <Badge
      variant="outline"
      className={cn(
        "text-[10px] font-semibold uppercase tracking-[0.1em]",
        status === "APPROVED" && "border-approve/40 bg-approve/10 text-approve",
        status === "PENDING_SIGNOFF" && "border-amber/50 bg-amber-dim/40 text-amber",
        status === "SUPERSEDED" && "text-muted-foreground",
      )}
    >
      {status.replace("_", " ")}
    </Badge>
  );
}

function formatDate(iso?: string) {
  if (!iso) return "—";
  // Fixed zone so an audit date reads the same for every viewer (and "…T00:00:00Z" isn't shown
  // as the previous day west of UTC).
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

export default function AttestationPage() {
  const { data, isPending, isError, error } = useAttestationPeriods();
  const periods = data ?? [];
  const [note, setNote] = useState("");
  const pending = periods.find((p) => p.status === "PENDING_SIGNOFF");
  const isSkipped = !isPending && !isError && data === null;

  const approveMutation = useApproveAttestation();
  const downloadMutation = useDownloadAttestation();

  return (
    <div>
      <PageHeader
        title="Attestation"
        subtitle="Month-end reports across Portfolio Performance, formally signed off, as a permanent audit/governance record, quick to download or share."
      />

      {isError ? (
        <div
          role="alert"
          className="mt-8 border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {error instanceof Error ? error.message : "Could not load attestation records."}
        </div>
      ) : isSkipped ? (
        <div className="mt-8 border border-border bg-card p-5 text-sm text-muted-foreground">
          Not Available Yet.
        </div>
      ) : (
        <>
          <Panel className="mt-8" title="Attestation Records">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Period</TableHead>
                  <TableHead>Report</TableHead>
                  <TableHead>Attested By</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isPending ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground">
                      Loading…
                    </TableCell>
                  </TableRow>
                ) : (
                  periods.map((p) => (
                    <TableRow key={p.periodId}>
                      <TableCell>{p.periodLabel}</TableCell>
                      <TableCell>{p.reportName}</TableCell>
                      <TableCell>{p.attestedBy ?? "—"}</TableCell>
                      <TableCell>{formatDate(p.attestedAt)}</TableCell>
                      <TableCell>
                        <StatusBadge status={p.status} />
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="gap-1.5"
                          disabled={p.status === "PENDING_SIGNOFF" || downloadMutation.isPending}
                          onClick={() => downloadMutation.mutate(p.periodId)}
                        >
                          <Download className="size-3.5" />
                          Download
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Panel>

          {pending ? (
            <Panel
              className="mt-6"
              title={`Sign Off — ${pending.periodLabel}`}
              description="Confirms the report was reviewed for this period; not a data-correction mechanism"
            >
              <Textarea
                placeholder="Optional note…"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="min-h-20"
              />
              <div className="mt-3">
                <Button
                  disabled={approveMutation.isPending}
                  onClick={() => {
                    approveMutation.mutate(
                      { periodId: pending.periodId, note },
                      { onSuccess: () => setNote("") },
                    );
                  }}
                >
                  Approve {pending.periodLabel}
                </Button>
              </div>
            </Panel>
          ) : null}
        </>
      )}

      <Panel className="mt-6" title="Governance Note">
        <p className="text-sm text-muted-foreground">
          Each attested record is a permanent entry: historical entries are never edited, only
          superseded by a new period&apos;s record. Attestation confirms the report was reviewed by Dan
          for that period; it is not a data-correction mechanism.
        </p>
      </Panel>
    </div>
  );
}
